import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import { fallbackGallery } from '../data';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const categories = ['Alle', 'Håndværk', 'Broderi', 'Kunst', 'Fællesskab'];

export default function GalleryPage() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    async function loadData() {
      setGalleryItems(fallbackGallery);
      setFilteredItems(fallbackGallery);
      setLoading(false);
    }
    loadData();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeCategory === 'Alle') {
      setFilteredItems(galleryItems);
    } else {
      setFilteredItems(
        galleryItems.filter(item => {
          const cat = item.category?.da || item.category;
          return cat === activeCategory;
        })
      );
    }
  }, [activeCategory, galleryItems]);

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Galleri & Palæstinensisk Broderi | DAPAO</title>
        <meta name="description" content="Se billeder af vores kulturudveksling, integrationsprojekter og smukt, håndlavet broderi fra kvinderne i Ein Kerem. Udforsk vores arbejde her." />
      </Helmet>

      <Navbar />

      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-12">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-4xl sm:text-6xl font-black text-black tracking-tight mb-6 uppercase"
            >
              Galleri
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-12"
            >
              Glimt fra vores arbejde, kulturudveksling og det stærke fællesskab vi bygger sammen.
            </motion.p>

            {/* Filter Bar */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-8 py-3 text-sm font-bold uppercase tracking-widest rounded-lg transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </header>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="flex gap-2">
                <div className="w-4 h-4 bg-red-600 rounded-full animate-bounce" />
                <div className="w-4 h-4 bg-green-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-4 h-4 bg-black rounded-full animate-bounce [animation-delay:-0.3s]" />
              </div>
            </div>
          ) : (
            <motion.div 
              layout
              className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8"
            >
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id || index}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative group overflow-hidden rounded-2xl break-inside-avoid shadow-sm hover:shadow-2xl transition-all duration-500 cursor-zoom-in"
                    onClick={() => setSelectedItem(item)}
                  >
                    <img
                      src={item.image[0]}
                      alt={item.alt || item.title?.da || 'Galleri billede'}
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                      <p className="text-white font-bold text-lg uppercase tracking-wider">
                        {item.title?.da}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          {!loading && filteredItems.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg italic">Ingen billeder fundet i denne kategori.</p>
            </div>
          )}
        </div>
      </main>

      <Dialog.Root open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/90 z-50 backdrop-blur-sm animate-in fade-in duration-300" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-5xl max-h-[90vh] outline-none animate-in zoom-in-95 duration-300">
            <div className="relative w-full h-full flex flex-col items-center">
              <Dialog.Close className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2 outline-none">
                <X size={32} />
              </Dialog.Close>
              
              {selectedItem && (
                <div className="w-full flex flex-col items-center">
                  <img
                    src={selectedItem.image[0]}
                    alt={selectedItem.alt || selectedItem.title?.da || 'Galleri billede'}
                    className="max-w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl"
                  />
                  <div className="mt-6 text-center text-white max-w-2xl">
                    <Dialog.Title className="text-2xl font-bold mb-2">
                      {selectedItem.title?.da}
                    </Dialog.Title>
                    <p className="text-red-500 font-bold uppercase tracking-widest text-sm mb-4">
                      {selectedItem.category?.da || selectedItem.category}
                    </p>
                    {selectedItem.description?.da && (
                      <Dialog.Description className="text-gray-300 text-lg">
                        {selectedItem.description.da}
                      </Dialog.Description>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Footer />
    </div>
  );
}
